import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAdminAuth } from '../auth/useAdminAuth';
import useAdminData from '../components/shared/useAdminData';
import { API_ENDPOINTS } from '../config/api-endpoints';
import SkeletonLoaderComponents from '../components/shared/SkeletonLoader';
import ErrorBoundary from '../components/shared/ErrorBoundary';
import './ViewAsUser.css';

const { Skeleton } = SkeletonLoaderComponents;

const ViewAsUser = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAdminAuth();
  const [activeTab, setActiveTab] = useState('chat-history');
  const [impersonationStartTime] = useState(new Date().toISOString());

  // Fetch user data
  const { data: userData, loading: userLoading, error: userError } = useAdminData(
    'user-profile',
    API_ENDPOINTS.USER.PROFILE,
    { params: { userId }, enabled: !!userId }
  );

  const { data: chatHistory, loading: chatLoading } = useAdminData(
    'chat-history',
    API_ENDPOINTS.CHAT.HISTORY,
    { params: { userId }, enabled: !!userId && activeTab === 'chat-history' }
  );

  const { data: userSettings, loading: settingsLoading } = useAdminData(
    'user-attributes',
    API_ENDPOINTS.USER.ATTRIBUTES,
    { params: { userId }, enabled: !!userId && activeTab === 'settings' }
  );

  const { data: masteryData, loading: masteryLoading } = useAdminData(
    'analytics-progress',
    API_ENDPOINTS.ANALYTICS.PROGRESS,
    { params: { userId }, enabled: !!userId && activeTab === 'mastery' }
  );

  const { data: uploads, loading: uploadsLoading } = useAdminData(
    'file-uploads',
    API_ENDPOINTS.FILES.FILE_MANAGER,
    { params: { userId }, enabled: !!userId && activeTab === 'uploads' }
  );

  // Log impersonation start
  useEffect(() => {
    if (userId && user) {
      logAuditEvent('IMPERSONATION_START', {
        targetUserId: userId,
        adminId: user.userId,
        adminEmail: user.email,
        timestamp: impersonationStartTime
      });
    }
  }, [userId, user, impersonationStartTime]);

  // Redirect if not SuperAdmin
  useEffect(() => {
    if (isAuthenticated && !user?.isSuperAdmin) {
      navigate('/');
    }
  }, [isAuthenticated, user, navigate]);

  const logAuditEvent = async (action, details) => {
    try {
      const response = await fetch(API_ENDPOINTS.ADMIN.AUDIT_LOGGER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getAuthToken()}`
        },
        body: JSON.stringify({
          action,
          targetType: 'user',
          targetId: userId,
          details,
          adminId: user.userId,
          timestamp: new Date().toISOString()
        })
      });

      if (!response.ok) {
        console.error('Failed to log audit event:', action);
      }
    } catch (error) {
      console.error('Error logging audit event:', error);
    }
  };

  const getAuthToken = async () => {
    try {
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      return session.tokens?.idToken?.toString() || '';
    } catch {
      return '';
    }
  };

  const handleExit = async () => {
    // Log impersonation end
    await logAuditEvent('IMPERSONATION_END', {
      targetUserId: userId,
      adminId: user.userId,
      adminEmail: user.email,
      startTime: impersonationStartTime,
      endTime: new Date().toISOString(),
      duration: Date.now() - new Date(impersonationStartTime).getTime()
    });

    navigate('/users');
  };

  // Intercept all write operations
  const handleBlockedAction = (actionName) => {
    alert(`Action blocked: ${actionName} is not allowed in View-As-User mode (read-only)`);
  };

  if (!isAuthenticated || !user?.isSuperAdmin) {
    return null;
  }

  if (userError) {
    return (
      <div className="view-as-user">
        <div className="view-as-user__error">
          <h2>Error Loading User</h2>
          <p>{userError.message || 'Failed to load user data'}</p>
          <button onClick={() => navigate('/users')} className="view-as-user__button">
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="view-as-user">
        {/* Persistent banner */}
        <div className="view-as-user__banner">
          <div className="view-as-user__banner-content">
            <div className="view-as-user__banner-icon">👁️</div>
            <div className="view-as-user__banner-text">
              <strong>View-As-User Mode (Read-Only)</strong>
              <span className="view-as-user__banner-user">
                Viewing as: {userData?.email || userId}
              </span>
            </div>
            <button 
              onClick={handleExit}
              className="view-as-user__exit-button"
            >
              Exit View-As-User
            </button>
          </div>
        </div>

        {/* User info header */}
        <div className="view-as-user__header">
          {userLoading ? (
            <Skeleton width="100%" height="80px" />
          ) : (
            <>
              <h1 className="view-as-user__title">
                {userData?.name || userData?.email || 'User Profile'}
              </h1>
              <div className="view-as-user__meta">
                <span className="view-as-user__meta-item">
                  User ID: {userId}
                </span>
                <span className="view-as-user__meta-item">
                  Email: {userData?.email}
                </span>
                <span className="view-as-user__meta-item">
                  Status: {userData?.status || 'Active'}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Tabs */}
        <div className="admin-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={activeTab === 'chat-history'}
            className={`admin-tab ${activeTab === 'chat-history' ? 'admin-tab--active' : ''}`}
            onClick={() => setActiveTab('chat-history')}
          >
            Chat History
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'settings'}
            className={`admin-tab ${activeTab === 'settings' ? 'admin-tab--active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'mastery'}
            className={`admin-tab ${activeTab === 'mastery' ? 'admin-tab--active' : ''}`}
            onClick={() => setActiveTab('mastery')}
          >
            Mastery Data
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'uploads'}
            className={`admin-tab ${activeTab === 'uploads' ? 'admin-tab--active' : ''}`}
            onClick={() => setActiveTab('uploads')}
          >
            Uploads
          </button>
        </div>

        {/* Tab content */}
        <div className="view-as-user__content">
          {activeTab === 'chat-history' && (
            <div className="view-as-user__section">
              <h2 className="view-as-user__section-title">Chat History</h2>
              {chatLoading ? (
                <>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} width="100%" height="100px" className="admin-skeleton-gap" />
                  ))}
                </>
              ) : chatHistory && chatHistory.length > 0 ? (
                <div className="view-as-user__chat-list">
                  {chatHistory.map((conversation, index) => (
                    <div key={index} className="view-as-user__chat-item">
                      <div className="view-as-user__chat-header">
                        <span className="view-as-user__chat-date">
                          {new Date(conversation.timestamp).toLocaleString()}
                        </span>
                        <span className="view-as-user__chat-topic">
                          {conversation.topic || 'General'}
                        </span>
                      </div>
                      <div className="view-as-user__chat-messages">
                        {conversation.messages?.map((msg, msgIndex) => (
                          <div 
                            key={msgIndex} 
                            className={`view-as-user__message view-as-user__message--${msg.role}`}
                          >
                            <strong>{msg.role === 'user' ? 'User' : 'AI'}:</strong> {msg.content}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="view-as-user__empty">No chat history available</p>
              )}
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="view-as-user__section">
              <h2 className="view-as-user__section-title">Settings & Preferences</h2>
              {settingsLoading ? (
                <>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} width="100%" height="40px" className="admin-skeleton-gap" />
                  ))}
                </>
              ) : userSettings ? (
                <div className="view-as-user__settings">
                  <div className="view-as-user__setting-group">
                    <h3>User Attributes</h3>
                    <dl className="view-as-user__definition-list">
                      {Object.entries(userSettings).map(([key, value]) => (
                        <React.Fragment key={key}>
                          <dt className="view-as-user__definition-term">{key}</dt>
                          <dd className="view-as-user__definition-value">
                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                          </dd>
                        </React.Fragment>
                      ))}
                    </dl>
                  </div>
                  <div className="view-as-user__readonly-notice">
                    ℹ️ All settings are read-only in View-As-User mode
                  </div>
                </div>
              ) : (
                <p className="view-as-user__empty">No settings available</p>
              )}
            </div>
          )}

          {activeTab === 'mastery' && (
            <div className="view-as-user__section">
              <h2 className="view-as-user__section-title">Mastery & Progress Data</h2>
              {masteryLoading ? (
                <>
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} width="100%" height="60px" className="admin-skeleton-gap" />
                  ))}
                </>
              ) : masteryData ? (
                <div className="view-as-user__mastery">
                  <div className="view-as-user__mastery-stats">
                    <div className="view-as-user__stat-card">
                      <div className="view-as-user__stat-value">
                        {masteryData.totalTopics || 0}
                      </div>
                      <div className="view-as-user__stat-label">Total Topics</div>
                    </div>
                    <div className="view-as-user__stat-card">
                      <div className="view-as-user__stat-value">
                        {masteryData.masteredTopics || 0}
                      </div>
                      <div className="view-as-user__stat-label">Mastered</div>
                    </div>
                    <div className="view-as-user__stat-card">
                      <div className="view-as-user__stat-value">
                        {masteryData.inProgressTopics || 0}
                      </div>
                      <div className="view-as-user__stat-label">In Progress</div>
                    </div>
                    <div className="view-as-user__stat-card">
                      <div className="view-as-user__stat-value">
                        {masteryData.overallProgress || 0}%
                      </div>
                      <div className="view-as-user__stat-label">Overall Progress</div>
                    </div>
                  </div>
                  {masteryData.topics && masteryData.topics.length > 0 && (
                    <div className="view-as-user__topic-list">
                      <h3>Topic Details</h3>
                      {masteryData.topics.map((topic, index) => (
                        <div key={index} className="view-as-user__topic-item">
                          <div className="view-as-user__topic-name">{topic.name}</div>
                          <div className="view-as-user__topic-progress">
                            <div 
                              className="view-as-user__topic-progress-bar"
                              style={{ width: `${topic.progress || 0}%` }}
                            />
                          </div>
                          <div className="view-as-user__topic-meta">
                            {topic.progress || 0}% • {topic.attempts || 0} attempts
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="view-as-user__empty">No mastery data available</p>
              )}
            </div>
          )}

          {activeTab === 'uploads' && (
            <div className="view-as-user__section">
              <h2 className="view-as-user__section-title">Uploaded Documents</h2>
              {uploadsLoading ? (
                <>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} width="100%" height="80px" className="admin-skeleton-gap" />
                  ))}
                </>
              ) : uploads && uploads.length > 0 ? (
                <div className="view-as-user__uploads-list">
                  {uploads.map((file, index) => (
                    <div key={index} className="view-as-user__upload-item">
                      <div className="view-as-user__upload-icon">
                        {file.type?.includes('image') ? '🖼️' : '📄'}
                      </div>
                      <div className="view-as-user__upload-info">
                        <div className="view-as-user__upload-name">{file.name}</div>
                        <div className="view-as-user__upload-meta">
                          {file.size ? `${(file.size / 1024).toFixed(2)} KB` : 'Unknown size'} • 
                          {file.uploadDate ? new Date(file.uploadDate).toLocaleDateString() : 'Unknown date'}
                        </div>
                      </div>
                      <button
                        className="view-as-user__upload-view"
                        onClick={() => handleBlockedAction('Download file')}
                      >
                        View (Read-Only)
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="view-as-user__empty">No uploaded files</p>
              )}
            </div>
          )}
        </div>

        {/* Write operation blockers - hidden form to intercept submissions */}
        <form 
          onSubmit={(e) => {
            e.preventDefault();
            handleBlockedAction('Form submission');
          }}
          style={{ display: 'none' }}
        />
      </div>
    </ErrorBoundary>
  );
};

export default ViewAsUser;
